import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toPreviewShareUrl } from "@/lib/shareUrl";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { Copy, ExternalLink, Trash2, MessageSquare, Save, X, Image as ImageIcon, Sparkles } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { LeadsTable } from "@/pages/DashboardOverview";

const DEFAULT_WA_MESSAGE = "I was referred to by {name} on ios.matrixshopping.com\nI would like to shop online and save money";

export default function LinkDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [editingMsg, setEditingMsg] = useState(false);
  const [msgDraft, setMsgDraft] = useState("");
  const [savingMsg, setSavingMsg] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Preview-image editing — pasted URL or data URL. Stored alongside the
  // link as og_image_url and surfaced by GET /api/p/<slug> for social
  // scrapers (WhatsApp / Facebook / iMessage).
  const [imgDraft, setImgDraft] = useState("");
  const [savingImg, setSavingImg] = useState(false);
  const [imgPreviewError, setImgPreviewError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/links/${id}/analytics`);
        setData(data);
        setMsgDraft(data.link.whatsapp_message || "");
        setImgDraft(data.link.og_image_url || "");
      } catch {
        toast.error("Link not found");
        navigate("/dashboard/links");
      }
    })();
  }, [id, navigate]);

  if (!data) return <DashboardLayout><div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" /></DashboardLayout>;

  const link = data.link;
  const isWhatsApp = /^https?:\/\/(www\.)?(wa\.me|api\.whatsapp\.com)\//i.test(link.destination_url || "");
  const effectiveMessage = ((link.whatsapp_message || "").trim() || DEFAULT_WA_MESSAGE).replaceAll("{name}", link.user_name || "you");
  const copy = () => copyToClipboard(toPreviewShareUrl(link.public_url), "Link copied");
  const requestDelete = () => setConfirmingDelete(true);
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/links/${link.link_id}`);
      toast.success("Deleted");
      navigate("/dashboard/links");
    } catch {
      toast.error("Failed to delete link");
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };
  const saveMessage = async () => {
    setSavingMsg(true);
    try {
      const { data: updated } = await api.patch(`/links/${link.link_id}`, { whatsapp_message: msgDraft });
      setData((d) => ({ ...d, link: { ...d.link, whatsapp_message: updated.whatsapp_message } }));
      toast.success("Message saved");
      setEditingMsg(false);
    } catch {
      toast.error("Failed to save message");
    } finally {
      setSavingMsg(false);
    }
  };

  const saveOgImage = async () => {
    setSavingImg(true);
    setImgPreviewError(false);
    try {
      const { data: updated } = await api.patch(`/links/${link.link_id}`, { og_image_url: imgDraft.trim() });
      setData((d) => ({ ...d, link: { ...d.link, og_image_url: updated.og_image_url } }));
      toast.success(updated.og_image_url ? "Preview image saved" : "Preview image cleared");
    } catch {
      toast.error("Couldn't save preview image");
    } finally {
      setSavingImg(false);
    }
  };

  // The URL we encourage affiliates to share: same redirect destination,
  // but goes through /api/p/<slug> so WhatsApp/Facebook get a rich preview
  // card (og:image, og:title). For /r/<slug> URLs the React SPA only has
  // generic site-wide meta tags, so scrapers never see the per-link image.
  const shareableUrl = toPreviewShareUrl(link.public_url);
  const dailyClicks = {};
  (data.recent_clicks || []).forEach((c) => {
    const d = (c.clicked_at || "").slice(0, 10);
    if (d) dailyClicks[d] = (dailyClicks[d] || 0) + 1;
  });
  const series = Object.keys(dailyClicks).sort().map((d) => ({ date: d, clicks: dailyClicks[d] }));

  return (
    <DashboardLayout>
      <RouterLink to="/dashboard/links" data-testid="back-to-links" className="label-eyebrow text-[#4B5563] hover:text-[#111827]">← All links</RouterLink>

      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-black text-[#111827]" data-testid="link-detail-name">{link.name}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-1 break-all" data-testid="link-detail-public-url">{shareableUrl}</code>
            <button onClick={() => copyToClipboard(shareableUrl, "Link copied")} data-testid="detail-copy-link" className="inline-flex items-center gap-1 text-xs font-bold text-[#002FA7] hover:underline"><Copy size={12} /> Copy</button>
            <a href={shareableUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#4B5563] hover:text-[#111827]"><ExternalLink size={12} /> Open</a>
          </div>
          <div className="text-[11px] text-[#9CA3AF] mt-1">
            Share this URL on WhatsApp/Facebook — it shows the preview thumbnail and still redirects shoppers through to the destination.
          </div>
          <div className="text-xs text-[#4B5563] mt-2">→ <span className="text-[#111827]">{link.destination_url}</span></div>
        </div>
        <button onClick={requestDelete} data-testid="detail-delete-link" className="btn-secondary !py-2 text-[#EF4444] border-[#FECACA] hover:border-[#EF4444]"><Trash2 size={14} /> Delete</button>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total clicks" value={data.total_clicks} />
        <Stat label="Unique" value={data.unique_clicks} />
        <Stat label="Leads" value={data.total_leads} />
        <Stat label="Conversions" value={data.total_conversions || 0} accent />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="card-flat p-6 lg:col-span-2">
          <div className="label-eyebrow text-[#4B5563] mb-3">Recent click trend</div>
          <div className="h-56">
            {series.length === 0 ? <div className="h-full grid place-items-center text-sm text-[#4B5563]">No clicks yet.</div> :
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ left: -20, right: 8, top: 8 }}>
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#4B5563" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Line type="monotone" dataKey="clicks" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            }
          </div>
        </div>
        <div className="card-flat p-6">
          <div className="label-eyebrow text-[#4B5563] mb-3">Countries</div>
          {data.countries.length === 0 ? <div className="text-sm text-[#4B5563]">No data yet.</div> : (
            <div className="space-y-3" data-testid="detail-countries">
              {data.countries.map((c) => (
                <div key={c.country_code + c.country} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-0.5">{c.country_code}</span>
                    <span className="text-sm">{c.country}</span>
                  </div>
                  <span className="font-display font-black text-sm">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Link preview image — controls the WhatsApp/Facebook social card */}
      <div className="card-flat p-6 mt-6" data-testid="og-image-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="label-eyebrow text-[#4B5563] mb-1 flex items-center gap-2"><ImageIcon size={12} /> Link preview thumbnail</div>
            <div className="font-display font-extrabold text-xl">Pick the picture that shows in WhatsApp / Facebook</div>
            <p className="text-xs text-[#4B5563] mt-1 max-w-xl leading-relaxed">
              Paste any image URL (e.g. right-click a product photo → "Copy image address"). It becomes the preview when you share <span className="font-mono text-[#065F46]">/api/p/{link.slug}</span> on social. Leave blank to use the site default.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-4 flex-col sm:flex-row">
          <div className="w-full sm:w-64 flex-shrink-0">
            {imgDraft && !imgPreviewError ? (
              <img
                src={imgDraft}
                alt="Preview thumbnail"
                onError={() => setImgPreviewError(true)}
                onLoad={() => setImgPreviewError(false)}
                referrerPolicy="no-referrer"
                data-testid="og-image-preview"
                className="w-full aspect-[1.91/1] object-cover rounded-md border border-[#E5E7EB] bg-[#F9FAFB]"
              />
            ) : (
              <div
                data-testid="og-image-placeholder"
                className="w-full aspect-[1.91/1] rounded-md border border-dashed border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF] flex flex-col items-center justify-center gap-1 text-xs"
              >
                <ImageIcon size={20} />
                <span>{imgPreviewError ? "Image didn't load" : "No image set"}</span>
              </div>
            )}
            <p className="text-[10px] text-[#9CA3AF] mt-1 font-mono">1.91:1 ratio looks best</p>
          </div>
          <div className="flex-1 min-w-0 w-full space-y-2">
            <input
              type="url"
              value={imgDraft}
              onChange={(e) => { setImgDraft(e.target.value); setImgPreviewError(false); }}
              placeholder="https://example.com/product.jpg"
              data-testid="og-image-input"
              className="input-base font-mono text-xs"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={saveOgImage}
                disabled={savingImg || imgDraft === (link.og_image_url || "")}
                data-testid="og-image-save"
                className="btn-primary !py-2 text-xs inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={12} /> {savingImg ? "Saving…" : "Save preview image"}
              </button>
              {link.og_image_url && (
                <button
                  onClick={() => { setImgDraft(""); setImgPreviewError(false); }}
                  data-testid="og-image-clear"
                  className="btn-secondary !py-2 text-xs inline-flex items-center gap-1"
                  type="button"
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>
            {link.og_image_url && (
              <div className="rounded-md bg-[#ECFDF5] border border-[#A7F3D0] px-3 py-2 text-xs text-[#065F46] flex items-start gap-2" data-testid="og-image-share-hint">
                <Sparkles size={12} className="mt-0.5 flex-shrink-0" />
                <span>
                  Use the <span className="font-bold">"With preview"</span> link above when sharing on WhatsApp/Facebook so this image shows up. Regular <span className="font-mono">/r/{link.slug}</span> links still work too, they just won't have a preview card.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {isWhatsApp && (
        <div className="card-flat p-6 mt-6" data-testid="whatsapp-message-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="label-eyebrow text-[#4B5563] mb-1 flex items-center gap-2"><MessageSquare size={12} /> Pre-filled WhatsApp message</div>
              <div className="font-display font-extrabold text-xl">What customers see when they land on WhatsApp</div>
            </div>
            {!editingMsg && (
              <button
                onClick={() => { setMsgDraft(link.whatsapp_message || ""); setEditingMsg(true); }}
                data-testid="edit-whatsapp-message-button"
                className="btn-secondary !py-2 text-xs"
              >
                Edit message
              </button>
            )}
          </div>

          {editingMsg ? (
            <div className="mt-4 space-y-3">
              <textarea
                data-testid="whatsapp-message-edit-input"
                rows={5}
                value={msgDraft}
                onChange={(e) => setMsgDraft(e.target.value)}
                placeholder={DEFAULT_WA_MESSAGE}
                className="input-base font-mono text-xs leading-relaxed"
              />
              <p className="text-xs text-[#9CA3AF]">
                Use <code className="font-mono bg-[#F3F4F6] px-1 rounded">{"{name}"}</code> to insert your name. Leave blank to use the default.
              </p>
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2 text-xs text-[#111827] whitespace-pre-wrap font-mono" data-testid="whatsapp-message-edit-preview">
                {((msgDraft.trim() || DEFAULT_WA_MESSAGE)).replaceAll("{name}", link.user_name || "you")}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={saveMessage} disabled={savingMsg} data-testid="save-whatsapp-message-button" className="btn-primary !py-2 text-xs inline-flex items-center gap-1"><Save size={12} /> {savingMsg ? "Saving…" : "Save"}</button>
                <button onClick={() => { setEditingMsg(false); setMsgDraft(link.whatsapp_message || ""); }} data-testid="cancel-whatsapp-message-button" className="btn-secondary !py-2 text-xs inline-flex items-center gap-1"><X size={12} /> Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-4 rounded-md bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-3 text-sm text-[#111827] whitespace-pre-wrap font-mono" data-testid="whatsapp-message-preview-current">
                {effectiveMessage}
              </div>
              {!link.whatsapp_message && (
                <p className="text-xs text-[#9CA3AF] mt-2">Using the default message. Click <span className="font-bold">Edit message</span> to personalize.</p>
              )}
            </>
          )}
        </div>
      )}

      <div className="card-flat p-6 mt-6">
        <div className="label-eyebrow text-[#4B5563] mb-1">Recent leads</div>
        <div className="font-display font-extrabold text-xl mb-4">Captured customers</div>
        <LeadsTable leads={data.recent_leads || []} />
      </div>

      <div className="card-flat p-6 mt-6">
        <div className="label-eyebrow text-[#4B5563] mb-1">Recent clicks</div>
        <div className="font-display font-extrabold text-xl mb-4">Click log</div>
        {(!data.recent_clicks || data.recent_clicks.length === 0) ? <div className="text-sm text-[#4B5563]">No clicks yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="clicks-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-[#4B5563]">
                  <th className="label-eyebrow font-bold py-3 pr-3">When</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Country</th>
                  <th className="label-eyebrow font-bold py-3 pr-3 hidden md:table-cell">City</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Lead?</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_clicks.map((c) => (
                  <tr key={c.click_id} className="border-b border-[#F3F4F6]">
                    <td className="py-3 pr-3 text-xs text-[#4B5563]">{new Date(c.clicked_at).toLocaleString()}</td>
                    <td className="py-3 pr-3"><span className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-0.5 mr-2">{c.country_code || "??"}</span>{c.country || "Unknown"}</td>
                    <td className="py-3 pr-3 hidden md:table-cell">{c.city || "—"}</td>
                    <td className="py-3 pr-3">{c.lead_captured ? <span className="text-[#002FA7] font-bold text-xs">CAPTURED</span> : <span className="text-[#9CA3AF] text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title="Delete this link?"
        description={`"${link.name}" — along with all its click and lead history — will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete link"
        busy={deleting}
        onCancel={() => !deleting && setConfirmingDelete(false)}
        onConfirm={confirmDelete}
      />
    </DashboardLayout>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="card-flat p-5">
      <div className="label-eyebrow text-[#4B5563]">{label}</div>
      <div className={`mt-2 font-display font-black text-3xl ${accent ? "text-[#002FA7]" : "text-[#111827]"}`}>{value}</div>
    </div>
  );
}
